/**
 * TopNav — workspace tabs + member chip + skin picker.
 * Per app-codex: surfaces "nav.workspace", "nav.member", "nav.skin", "nav.notifications".
 * Dev mode: shows a "Dev" pill and a member switcher; both are dev-only.
 */
import { useState, useRef, useEffect } from 'react'
import { useApp } from '../store'
import { SEED_MEMBERS } from '../seed'
import { CalendarDays, MessageCircle, BookOpen, History, Award, ListTodo, Sparkles, Beaker, X, ChevronDown } from 'lucide-react'

const TABS = [
  { id: 'tasks' as const, label: 'Today', icon: ListTodo },
  { id: 'calendar' as const, label: 'Calendar', icon: CalendarDays },
  { id: 'chat' as const, label: 'Chat', icon: MessageCircle },
  { id: 'resources' as const, label: 'Resources', icon: BookOpen },
  { id: 'history' as const, label: 'History', icon: History },
  { id: 'achievements' as const, label: 'Awards', icon: Award },
] as const

export function TopNav() {
  const { activeWorkspace, setActiveWorkspace, currentMember, setCurrentMember, skin, setSkin, dev, setDev } = useApp()
  const [memberMenuOpen, setMemberMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMemberMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  return (
    <nav className="border-b border-slate-200 bg-white sticky top-0 z-20">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-garden-800 text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </div>
            <span className="font-semibold text-skin-text hidden sm:inline">Home-studio</span>
            {dev && (
              <span className="ml-1 inline-flex items-center gap-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">
                <Beaker className="w-3 h-3" /> Dev
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 overflow-x-auto">
            {TABS.map(({ id, label, icon: Icon }) => {
              const active = activeWorkspace === id
              return (
                <button
                  key={id}
                  onClick={() => setActiveWorkspace(id)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-touch ${
                    active
                      ? 'bg-garden-100 text-garden-800'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden md:inline">{label}</span>
                </button>
              )
            })}
          </div>

          <div className="flex items-center gap-2">
            <select
              value={skin}
              onChange={(e) => setSkin(e.target.value as any)}
              className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 min-h-touch bg-white"
              aria-label="Theme"
            >
              <option value="garden">Garden</option>
              <option value="journey">Journey</option>
              <option value="light">Light</option>
            </select>
            {currentMember && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMemberMenuOpen((v) => !v)}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 min-h-touch"
                >
                  <div className="w-7 h-7 rounded-full bg-garden-200 text-garden-800 flex items-center justify-center text-sm font-medium">
                    {currentMember.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm text-skin-text hidden sm:inline">{currentMember.name}</span>
                  <ChevronDown className="w-3 h-3 text-slate-400 hidden sm:block" />
                </button>
                {memberMenuOpen && dev && (
                  <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1">
                    <div className="px-3 py-2 text-xs text-slate-500 uppercase tracking-wide">View as</div>
                    {SEED_MEMBERS.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => {
                          setCurrentMember(m)
                          setMemberMenuOpen(false)
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 ${
                          currentMember.id === m.id ? 'bg-garden-50 text-garden-800' : ''
                        }`}
                      >
                        <div className="w-6 h-6 rounded-full bg-garden-200 text-garden-800 flex items-center justify-center text-xs font-medium">
                          {m.name.charAt(0)}
                        </div>
                        <span>{m.name}</span>
                        <span className="ml-auto text-xs text-slate-400">{m.role}</span>
                      </button>
                    ))}
                    <div className="border-t border-slate-200 mt-1 pt-1">
                      <button
                        onClick={() => {
                          setDev(false)
                          setMemberMenuOpen(false)
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-600"
                      >
                        <X className="w-4 h-4" />
                        Exit Dev mode
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {!dev && (
              <button
                onClick={() => setDev(true)}
                className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1.5 rounded-lg hover:bg-slate-100 min-h-touch"
                title="Enter Dev mode — local seed data, no Supabase"
              >
                Dev
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}
